import { CaretLeftIcon, CaretRightIcon, WarningIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { formatNumber } from "../../lib/format";
import { Button } from "./button";

const tones = {
  neutral: "border-line bg-raised text-body",
  success: "border-accent-line bg-accent-bg text-accent-text",
  warning: "border-gold-line bg-gold-bg text-gold-text",
  danger: "border-danger-line bg-danger-bg text-danger-text",
  info: "border-info-line bg-info-bg text-info-text",
} as const;

export type Tone = keyof typeof tones;

export function Badge({
  tone = "neutral",
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "text-caption inline-flex h-5.5 items-center rounded-full border px-2 font-medium whitespace-nowrap",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Card({
  title,
  eyebrow,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("rounded-card border-line bg-surface border", className)}>
      {(title || actions) && (
        <header className="border-line flex items-center justify-between gap-3 border-b px-4 py-3">
          <div>
            {eyebrow && (
              <p className="text-micro text-faint mb-0.5 font-semibold uppercase">{eyebrow}</p>
            )}
            <h2 className="text-ui-lg text-ink font-semibold">{title}</h2>
          </div>
          {actions}
        </header>
      )}
      <div className={cn("p-4", bodyClassName)}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-prose">
        <h1 className="display text-title text-ink">{title}</h1>
        {description && <p className="text-caption text-muted mt-1.5">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Filters live in their own tray so the table below starts clean. */
export function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-panel border-line bg-surface/60 mb-3 flex flex-wrap items-center gap-2 border p-2">
      {children}
    </div>
  );
}

export function EmptyState({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <div className="px-4 py-14 text-center">
      {title && <p className="text-ui-lg text-ink mb-1 font-medium">{title}</p>}
      <p className="text-caption text-muted mx-auto max-w-sm">{children}</p>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="rounded-panel border-danger-line bg-danger-bg text-danger-text flex items-center gap-3 border px-4 py-3">
      <WarningIcon className="size-4 shrink-0" />
      <span className="text-caption flex-1">
        {error instanceof Error ? error.message : "This did not load. Try again."}
      </span>
      {onRetry && (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "border-line border-t-accent inline-block size-4 animate-spin rounded-full border-2",
        className,
      )}
    />
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="text-caption text-muted flex items-center gap-2.5 px-4 py-14">
      <Spinner /> {label}…
    </div>
  );
}

/**
 * Numbers are set in the display face with the site's negative tracking — the strongest
 * continuity cue between the console and the landing page.
 */
export function StatCard({
  label,
  value,
  hint,
  tone,
  accent,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-card border-line bg-surface relative overflow-hidden border px-4 py-3.5",
        accent && "border-accent-line bg-accent-bg/40",
      )}
    >
      <p className="text-micro text-faint font-semibold uppercase">{label}</p>
      <p
        className={cn(
          "display tabular text-stat mt-2",
          tone === "danger"
            ? "text-danger-text"
            : tone === "warning"
              ? "text-gold-text"
              : accent
                ? "text-accent"
                : "text-ink",
        )}
      >
        {value}
      </p>
      {hint && <p className="text-caption text-muted mt-1.5">{hint}</p>}
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="border-line text-caption text-muted flex items-center justify-between gap-3 border-t px-3 py-2">
      <span className="tabular">
        {formatNumber(from)}–{formatNumber(to)} of {formatNumber(total)}
      </span>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label="Previous page"
        >
          <CaretLeftIcon />
        </Button>
        <span className="tabular text-body px-1">
          {page} / {pages}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          aria-label="Next page"
        >
          <CaretRightIcon />
        </Button>
      </div>
    </div>
  );
}

/** Segmented pill control; the selected segment is the only filled thing in the row. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  className,
}: {
  tabs: { id: T; label: ReactNode }[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        "border-line bg-well mb-5 inline-flex gap-0.5 rounded-full border p-1",
        className,
      )}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === value}
          onClick={() => onChange(tab.id)}
          className={cn(
            "ease-out-expo text-ui rounded-full px-3.5 py-1.5 transition-colors duration-200",
            tab.id === value
              ? "bg-accent text-accent-ink font-semibold"
              : "text-muted hover:text-ink hover:bg-raised",
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "text-micro text-faint h-9 px-3 font-semibold tracking-wide whitespace-nowrap uppercase",
        className,
      )}
    >
      {children}
    </th>
  );
}
